import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useWindows } from '../../contexts/WindowContext';
import { getGradientClasses } from '../../utils/helpers';
import { PlayIcon, CodeIcon, LayoutIcon, SparklesIcon, BrainIcon, MessageCircleIcon, ArrowDownIcon, UserIcon, FolderIcon, ChevronDownIcon } from 'lucide-react';
export function MobileWidgets() {
  const {
    getThemeClasses,
    theme
  } = useTheme();
  const {
    openWindow
  } = useWindows();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  // Same showcase items as desktop
  const showcaseItems = [{
    type: 'video',
    title: 'AI Character Animation',
    subtitle: 'Kling AI + Flux Pro',
    thumbnail: 'https://images.pexels.com/photos/8728380/pexels-photo-8728380.jpeg?auto=compress&cs=tinysrgb&w=400',
    color: 'from-rose-500 to-pink-600',
    rotation: -2,
    app: 'portfolio'
  }, {
    type: 'ui',
    title: 'BULKRR App Design',
    subtitle: 'AI-driven Shopping',
    image: 'https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=400',
    color: 'from-emerald-500 to-teal-600',
    rotation: 3,
    app: 'portfolio'
  }, {
    type: 'prototype',
    title: 'Interactive Dashboard',
    subtitle: 'React + AI Integration',
    image: 'https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&w=400',
    color: 'from-blue-500 to-indigo-600',
    rotation: -1,
    app: 'portfolio'
  }, {
    type: 'ui',
    title: 'Camping App UX',
    subtitle: 'Group Experience Design',
    image: 'https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=400',
    color: 'from-sky-500 to-blue-600',
    rotation: 2,
    app: 'portfolio'
  }, {
    type: 'code',
    title: 'AI Prompt Engineering',
    subtitle: 'Character System',
    color: 'from-purple-500 to-violet-600',
    rotation: -3,
    app: 'expertise',
    codeSnippet: `// AI Character Prompt
const prompt = \`Create a character with:
- Personality: Curious explorer
- Visual: Cyberpunk aesthetic
- Emotion: Wonder and excitement
\``
  }, {
    type: 'video',
    title: 'Motion Design',
    subtitle: 'Micro-interactions',
    thumbnail: 'https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=400',
    color: 'from-amber-500 to-orange-600',
    rotation: 1,
    app: 'process'
  }];
  return <div className="h-full overflow-y-auto pb-24 px-4 pt-6" data-id="element-786">
      {/* Hero Section - matching desktop structure */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="text-center mb-6" data-id="element-787">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2
      }} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${themeClasses.card} border ${themeClasses.border} backdrop-blur-xl mb-4 shadow-lg`} data-id="element-788">
          <SparklesIcon className="w-3.5 h-3.5 text-purple-500" data-id="element-789" />
          <span className={`text-xs font-medium ${themeClasses.text}`} data-id="element-790">
            UX Designer & AI Developer
          </span>
        </motion.div>
        {/* Main Title */}
        <h1 className={`text-2xl font-bold mb-3 tracking-tight ${themeClasses.text}`} data-id="element-791">
          Van Complexe Vraag naar
          <br data-id="element-792" />
          <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 bg-clip-text text-transparent" data-id="element-793">
            Elegante Oplossing
          </span>
        </h1>
        {/* Subtitle */}
        <p className={`text-sm ${themeClasses.text} opacity-70 max-w-md mx-auto mb-5 leading-relaxed`} data-id="element-794">
          Ik combineer design, AI en development om complexe uitdagingen te
          vertalen naar intuïtieve digitale ervaringen die gebruikers begrijpen
          en waarderen.
        </p>
        {/* CTA Buttons - same 3 buttons as desktop */}
        <div className="flex flex-col gap-2 mb-4" data-id="element-795">
          <button onClick={() => openWindow('about', 'About Mick')} className={`w-full px-4 py-3 rounded-xl ${themeClasses.card} border-2 ${themeClasses.border} ${themeClasses.text} font-semibold transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg backdrop-blur-xl text-sm`} data-id="element-796">
            <UserIcon className="w-4 h-4" data-id="element-797" />
            Lees meer over mij
          </button>
          <button onClick={() => openWindow('portfolio', 'Portfolio')} className="w-full px-4 py-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white font-semibold transition-all active:scale-95 shadow-lg flex items-center justify-center gap-2 text-sm" data-id="element-798">
            <FolderIcon className="w-4 h-4" data-id="element-799" />
            Bekijk Portfolio
          </button>
          <button onClick={() => openWindow('contact', 'Contact')} className={`w-full px-4 py-3 rounded-xl ${themeClasses.card} border-2 ${themeClasses.border} ${themeClasses.text} font-semibold transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg backdrop-blur-xl text-sm`} data-id="element-800">
            <MessageCircleIcon className="w-4 h-4" data-id="element-801" />
            Start Gesprek
          </button>
        </div>
        {/* Scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className={`flex flex-col items-center gap-1 ${themeClasses.text} opacity-50`} data-id="element-802">
          
          <motion.div animate={{
          y: [0, 6, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-803">
            <ArrowDownIcon className="w-3 h-3" data-id="element-804" />
          </motion.div>
        </motion.div>
      </motion.div>
      {/* Featured Work Section - matching desktop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.4
    }} className="mb-4" data-id="element-805">
        <div className="flex items-center justify-between mb-4" data-id="element-806">
          
          
        </div>
      </motion.div>
      {/* Showcase Cards - vertical stack with pinboard style */}
      <div className="space-y-4 mb-6" data-id="element-807">
        {showcaseItems.map((item, index) => <motion.div key={index} initial={{
        opacity: 0,
        scale: 0.9,
        rotate: 0
      }} animate={{
        opacity: 1,
        scale: hoveredItem === index ? 1.02 : 1,
        rotate: item.rotation
      }} transition={{
        delay: 0.5 + index * 0.1,
        type: 'spring',
        stiffness: 260,
        damping: 20
      }} onTouchStart={() => setHoveredItem(index)} onTouchEnd={() => setHoveredItem(null)} onClick={() => openWindow(item.app, item.title)} className="relative" data-id="element-808">
            <div className={`relative h-48 rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
          boxShadow: hoveredItem === index ? '0 20px 40px -10px rgba(0, 0, 0, 0.3)' : '0 8px 20px -8px rgba(0, 0, 0, 0.2)'
        }} data-id="element-809">
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-20`} data-id="element-810" />
              {/* Content based on type */}
              {item.type === 'video' && <>
                  <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" data-id="element-811" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center" data-id="element-812">
                    <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center`} data-id="element-813">
                      <PlayIcon className="w-7 h-7 text-white ml-1" data-id="element-814" />
                    </div>
                  </div>
                </>}
              {item.type === 'ui' && item.image && <>
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-815" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-30 mix-blend-multiply`} data-id="element-816" />
                </>}
              {item.type === 'prototype' && item.image && <>
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-817" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-818" />
                </>}
              {item.type === 'code' && item.codeSnippet && <div className="w-full h-full p-5 flex flex-col justify-center" data-id="element-819">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3`} data-id="element-820">
                    <CodeIcon className="w-5 h-5 text-white" data-id="element-821" />
                  </div>
                  <pre className={`text-xs ${themeClasses.text} opacity-70 font-mono overflow-hidden line-clamp-4`} data-id="element-822">
                    {item.codeSnippet}
                  </pre>
                </div>}
              {/* Label overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent" data-id="element-823">
                <div className="flex items-center gap-2 mb-1" data-id="element-824">
                  {item.type === 'video' && <PlayIcon className="w-4 h-4 text-white" data-id="element-825" />}
                  {item.type === 'ui' && <LayoutIcon className="w-4 h-4 text-white" data-id="element-826" />}
                  {item.type === 'prototype' && <SparklesIcon className="w-4 h-4 text-white" data-id="element-827" />}
                  {item.type === 'code' && <CodeIcon className="w-4 h-4 text-white" data-id="element-828" />}
                  <h3 className="text-white font-bold text-sm" data-id="element-829">{item.title}</h3>
                </div>
                <p className="text-white/70 text-xs" data-id="element-830">{item.subtitle}</p>
              </div>
              {/* Hover indicator */}
              {hoveredItem === index && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="absolute inset-0 border-4 border-white/20 rounded-2xl pointer-events-none" data-id="element-831" />}
            </div>
            {/* Pin effect */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-832" />
          </motion.div>)}
      </div>
      {/* Menu indicator - matching desktop */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2
    }} className="mb-6" data-id="element-833">
        <div className={`px-4 py-2.5 rounded-xl ${themeClasses.card} border ${themeClasses.border} backdrop-blur-xl flex items-center gap-2 shadow-lg`} data-id="element-834">
          <div className="flex items-center gap-2" data-id="element-835">
            <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" data-id="element-836" />
            <span className={`text-xs ${themeClasses.text} opacity-70 font-medium`} data-id="element-837">
              Gebruik het menu onderaan voor meer opties
            </span>
            <motion.div animate={{
            y: [0, 3, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-838">
              <ChevronDownIcon className={`w-4 h-4 ${themeClasses.text} opacity-70`} data-id="element-839" />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>;
}